import { useQueryClient } from "@tanstack/react-query";
import type { Document, Folder } from "certiblok-api-manager";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { subscribe, useSnapshot } from "valtio";
import { useTreeViewContext } from "../TreeViewController";
import { treeViewQueryKeys } from "../queries/treeViewQueries";
import type { TreeViewListItem } from "../types/ListItem";
import type { TreeViewOpenedFolder } from "../types/OpenedFolder";

export const useTreeViewOpenedFolders = () => {
	const { itemsList, rootFolderId } = useTreeViewContext();
	const itemsListSnapshot = useSnapshot(itemsList);
	return useMemo(() => {
		const openedFolders: { item: TreeViewListItem; index: number; id: string }[] = [];
		itemsListSnapshot.forEach((item, index) => {
			let areAllLevelsOpened = true;
			let parentFolder: (typeof item)["parentFolder"] | undefined = item.parentFolder;
			while (parentFolder && parentFolder?.folderId !== rootFolderId) {
				if (!parentFolder?.isExpanded) {
					areAllLevelsOpened = false;
					break;
				}
				// eslint-disable-next-line no-loop-func
				parentFolder = itemsListSnapshot.find((item) => item.id === parentFolder?.id)?.parentFolder;
			}
			if (areAllLevelsOpened) {
				openedFolders.push({ item: itemsList[index], index, id: item.id });
			}
		});
		return openedFolders;
	}, [itemsList, itemsListSnapshot, rootFolderId]);
};

export function useFolderStatus(folderId: string) {
	const { openedFolders } = useTreeViewContext();

	return useSnapshot(openedFolders).get(folderId);
}

export function useToggleOpenFolder() {
	const { itemsList, openedFolders, lastSelectedFolder, ignoreQueryParams } = useTreeViewContext();

	const [searchParams, setSearchParams] = useSearchParams();

	return useCallback(
		({
			folder,
			parentFolder,
			forceOpen,
		}: {
			folder: { folderId: string; childrenCounts: number };
			parentFolder: TreeViewOpenedFolder;
			forceOpen?: boolean;
		}) => {
			const { folderId, childrenCounts } = folder;
			const folderIndex = itemsList.findIndex((item) => item.id === folderId);
			const openedFolder = openedFolders.get(folderId);
			const actualFolder = itemsList[folderIndex].item as unknown as Folder;
			const searchParamsOpenedFolders = searchParams.get("openedFolders");
			const searchParamsOpenedFoldersArray = searchParamsOpenedFolders
				? searchParamsOpenedFolders.trim().replaceAll(" ", "").split("#")
				: [];
			const folderSearchParamsIndex = searchParamsOpenedFoldersArray?.findIndex((elem) => {
				const jsonElem = JSON.parse(elem) as { id: string; deep: number };
				return jsonElem.id === actualFolder.id && jsonElem.deep === parentFolder.deepness + 1;
			});
			const isAlreadyInSearchParams = folderSearchParamsIndex !== undefined && folderSearchParamsIndex >= 0;
			const elementToPushInSearch = JSON.stringify({ id: actualFolder.id, deep: parentFolder.deepness + 1 });
			let hasEditedSearchParams = false;
			if (openedFolder) {
				if (!forceOpen) {
					lastSelectedFolder.value = openedFolder.isExpanded ? undefined : actualFolder;
				}
				openedFolder.isExpanded = !openedFolder.isExpanded || Boolean(forceOpen);
				if (openedFolder.isExpanded) {
					if (!isAlreadyInSearchParams) {
						searchParamsOpenedFoldersArray.push(elementToPushInSearch);
						hasEditedSearchParams = true;
					}
				} else {
					if (folderSearchParamsIndex !== undefined && folderSearchParamsIndex >= 0) {
						searchParamsOpenedFoldersArray.splice(folderSearchParamsIndex, 1);
						hasEditedSearchParams = true;
					}
				}
			} else {
				if (!forceOpen) {
					lastSelectedFolder.value = actualFolder;
				}
				const newParentFolder = {
					folderId: actualFolder.id,
					deepness: parentFolder.deepness + 1,
					isExpanded: true,
					totalItems: childrenCounts,
					id: folderId,
				};
				openedFolders.set(folderId, newParentFolder);
				itemsList.splice(
					folderIndex + 1,
					0,
					...Array.from(Array(childrenCounts)).map<TreeViewListItem>(() => {
						return {
							parentFolder: newParentFolder,
							isLoading: true,
							id: nanoid(),
						};
					})
				);
				if (!isAlreadyInSearchParams) {
					searchParamsOpenedFoldersArray.push(elementToPushInSearch);
					hasEditedSearchParams = true;
				}
			}
			if (hasEditedSearchParams && !ignoreQueryParams) {
				searchParams.set("openedFolders", searchParamsOpenedFoldersArray.join("#"));
				setSearchParams(searchParams, {
					replace: true,
				});
			}
		},
		[itemsList, lastSelectedFolder, openedFolders, searchParams, setSearchParams]
	);
}

export function useSelectedFolderProxy() {
	const { lastSelectedFolder } = useTreeViewContext();
	return lastSelectedFolder;
}

export function useIsFolderSelected(folderId: string) {
	const [isSelected, setIsSelected] = useState(false);
	const { lastSelectedFolder } = useTreeViewContext();

	useEffect(() => {
		const isSelectedCallback = () => {
			setIsSelected(lastSelectedFolder.value?.id === folderId);
		};
		isSelectedCallback();
		return subscribe(lastSelectedFolder, isSelectedCallback);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [folderId]);

	return isSelected;
}

export function useAddItemsToFolder() {
	const { itemsList, openedFolders } = useTreeViewContext();
	const queryClient = useQueryClient();

	return useCallback(
		({
			parentFolderId,
			items,
			isFolder,
		}: { parentFolderId: string } & ({ isFolder: true; items: Folder[] } | { isFolder?: never; items: Document[] })) => {
			const folderItem = itemsList.find((item) => item.item?.id === parentFolderId);
			const parentFolder = openedFolders.get(folderItem?.id ?? parentFolderId);

			if (!parentFolder) return;

			const parentFolderIndex = itemsList.findIndex((item) => item.item?.id === parentFolderId);
			let lastParentFolderItemIndex = parentFolderIndex + 1;
			let found = false;

			while (!found && lastParentFolderItemIndex < itemsList.length) {
				const currentItem = itemsList[lastParentFolderItemIndex];
				const currentParentFolder = currentItem.parentFolder;

				if (
					(currentParentFolder.deepness === parentFolder.deepness &&
						(parentFolder.folderId !== parentFolderId || (isFolder && !currentItem.item?.isFolder))) ||
					currentParentFolder.deepness < parentFolder.deepness
				) {
					found = true;
				} else lastParentFolderItemIndex++;
			}

			parentFolder.totalItems += items.length;
			itemsList.splice(
				lastParentFolderItemIndex,
				0,
				...items.map<TreeViewListItem>((item) => ({
					parentFolder,
					item: { ...item, isFolder: isFolder ?? false, companyId: "" },
					isLoading: false,
					id: nanoid(),
				}))
			);

			queryClient.invalidateQueries(treeViewQueryKeys.base);
		},
		[itemsList, openedFolders, queryClient]
	);
}
